import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { APP_VERSION, VERSION_NOTES } from '../config/version';
import firebaseService from '../services/firebaseService';
import { FireSQL } from 'firesql';
import QRCodeGeneratorWithHistory from './QRCodeGenerator';
import GameConfig from './GameConfig';
import toast from 'react-hot-toast';
import analytics from '../utils/analytics';
import ReactJson from 'react-json-view';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import './AdminDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Joined-date presets for the users filter. Values are resolved to exact
// local-day ISO instants by getJoinedRangeBounds (server expects joinedAfter
// / joinedBefore and does not know about these keys).
const USER_JOINED_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: '7 days to today' },
  { value: '30d', label: '30 days to today' },
  { value: '3m', label: '3 months to today' },
  { value: '6m', label: '6 months to today' },
  { value: '1y', label: '1 year to today' },
];

// Resolve a preset to { joinedAfter, joinedBefore } ISO strings, or empty
// strings for "all time". Day boundaries are local, so a user who joined at
// 11pm local is included on the correct day regardless of the server's TZ.
function getJoinedRangeBounds(range) {
  if (!range || range === 'all') return { joinedAfter: '', joinedBefore: '' };

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
  };
  const monthsAgo = (n) => {
    const d = new Date();
    d.setMonth(d.getMonth() - n);
    return d;
  };

  let start;
  let end = endOfDay(new Date());

  switch (range) {
    case 'today':
      start = startOfDay(new Date());
      break;
    case 'yesterday':
      start = startOfDay(daysAgo(1));
      end = endOfDay(daysAgo(1));
      break;
    case '7d':
      start = startOfDay(daysAgo(6)); // includes today
      break;
    case '30d':
      start = startOfDay(daysAgo(29));
      break;
    case '3m':
      start = startOfDay(monthsAgo(3));
      break;
    case '6m':
      start = startOfDay(monthsAgo(6));
      break;
    case '1y':
      start = startOfDay(monthsAgo(12));
      break;
    default:
      return { joinedAfter: '', joinedBefore: '' };
  }

  return { joinedAfter: start.toISOString(), joinedBefore: end.toISOString() };
}

function AdminDashboard() {
  const { currentUser: user, logout } = useAuth();
  const { restrooms, deleteRestroom, deleteUser, verifyRestroom, fetchRestrooms } = useData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(null);
  const [stats, setStats] = useState({
    totalRestrooms: 0,
    verifiedRestrooms: 0,
    totalUsers: 0,
    totalReviews: 0
  });
  const [restroomFilter, setRestroomFilter] = useState('all'); // 'all', 'verified', 'unverified'
  const [restroomSearch, setRestroomSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedRestrooms, setSelectedRestrooms] = useState([]); // For bulk operations
  const [restroomSort, setRestroomSort] = useState({ column: 'date', direction: 'desc' });
  const [userSort, setUserSort] = useState({ column: 'date', direction: 'desc' });
  // Phase 1: users list starts empty — admins search via SearchUsers instead
  // of downloading the whole collection. 'any' | 'yes' | 'no' for reviewed.
  // Joined date is a preset range (see USER_JOINED_RANGES) rather than a
  // from/to pair; the client resolves it to exact ISO instants.
  const [userFilters, setUserFilters] = useState({
    email: '',
    username: '',
    name: '',
    joinedRange: 'all',
    hasReviewed: 'any'
  });
  const [userSearching, setUserSearching] = useState(false);
  const [hasSearchedUsers, setHasSearchedUsers] = useState(false);
  // Phase 2: admin broadcast composer.
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyText, setNotifyText] = useState('');
  const [notifyChannels, setNotifyChannels] = useState('inbox'); // 'inbox' | 'push' | 'both'
  const [notifySendToAll, setNotifySendToAll] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  // Phase 4: broadcast log viewer.
  const [showBroadcasts, setShowBroadcasts] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [broadcastsPage, setBroadcastsPage] = useState(1);
  const [broadcastsHasMore, setBroadcastsHasMore] = useState(false);
  // Bounded recent-users fetch for the overview Recent Activity feed only
  // (the Users table itself stays empty until searched).
  const [recentUsers, setRecentUsers] = useState([]);
  
  // FireSQL state
  const [sqlQuery, setSqlQuery] = useState('');
  const [queryResults, setQueryResults] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState(null);

  // Restroom editing state
  const [editingRestroom, setEditingRestroom] = useState(null);
  const [editRestroomName, setEditRestroomName] = useState('');
  const [editCategoryValue, setEditCategoryValue] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  // AreYouAt matching state
  const [matchingRestroom, setMatchingRestroom] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  
  // Migration state - commented out since all users are migrated
  // const [migrationUsername, setMigrationUsername] = useState('');
  // const [migrationBatch, setMigrationBatch] = useState('');
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResults, setMigrationResults] = useState([]);
  // const [userDocMigrationStatus, setUserDocMigrationStatus] = useState(null);
  // const [migrationStatus, setMigrationStatus] = useState(null);
  
  // Review ID Migration state (hidden for production security)
  // const [reviewIdMigrationStatus, setReviewIdMigrationStatus] = useState(null);
  // const [reviewIdMigrationLoading, setReviewIdMigrationLoading = useState(false);
  // const [reviewIdMigrationResults, setReviewIdMigrationResults] = useState(null);

  // New state for registration stats
  const [registrationStats, setRegistrationStats] = useState(null);
  const [registrationStatsLoading, setRegistrationStatsLoading] = useState(false);
  const [registrationStatsError, setRegistrationStatsError] = useState(null);

  // New state for restroom metrics
  const [restroomsAddedStats, setRestroomsAddedStats] = useState(null);
  const [restroomsVerifiedStats, setRestroomsVerifiedStats] = useState(null);
  const [restroomMetricsLoading, setRestroomMetricsLoading] = useState(false);
  const [restroomMetricsError, setRestroomMetricsError] = useState(null);

  // Data Maintenance: TP Leaderboard refresh state
  const [refreshTpLoading, setRefreshTpLoading] = useState(false);
  const [lastRefreshInfo, setLastRefreshInfo] = useState(null);

  // Data Maintenance: Gamification rules seeding state
  const [seedRulesLoading, setSeedRulesLoading] = useState(false);
  const [seedForceOverwrite, setSeedForceOverwrite] = useState(false);
  const [lastSeedInfo, setLastSeedInfo] = useState(null);

  // Data Maintenance: Gamification backfill state
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillLimit, setBackfillLimit] = useState(200);
  const [backfillResume, setBackfillResume] = useState(true);
  const [backfillResetCheckpoint, setBackfillResetCheckpoint] = useState(false);
  const [lastBackfillInfo, setLastBackfillInfo] = useState(null);


  // Handle clicking outside while editing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingRestroom && 
          !event.target.closest('.restroom-name') && 
          !event.target.closest('td:nth-child(3)') && // Category column
          !event.target.closest('.action-buttons') &&
          !event.target.closest('.edit-btn')) {
        handleCancelEditRestroom();
      }
    };

    if (editingRestroom) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingRestroom]);

  // Load restrooms from Firebase + total user count (cheap aggregate).
  // The users table itself starts empty — see handleSearchUsers.
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchRestrooms(),
          (async () => {
            try {
              const functions = getFunctions(firebaseService.app);
              const getUsersCount = httpsCallable(functions, 'GetUsersCount');
              const result = await getUsersCount({
                database_id_hash: localStorage.getItem('database_id_hash') || ''
              });
              if (result.data?.success) setTotalUserCount(result.data.count);
            } catch (countError) {
              console.error('Error fetching user count:', countError);
            }
          })(),
          (async () => {
            try {
              const functions = getFunctions(firebaseService.app);
              const searchUsers = httpsCallable(functions, 'SearchUsers');
              const result = await searchUsers({
                limit: 20,
                database_id_hash: localStorage.getItem('database_id_hash') || ''
              });
              if (result.data?.success) setRecentUsers(result.data.users);
            } catch (recentError) {
              console.error('Error fetching recent users:', recentError);
            }
          })()
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      }
    };
    fetchData();
  }, [fetchRestrooms]);

  // Fetch metrics data when metrics tab is active
  useEffect(() => {
    if (activeTab === 'metrics' && user?.isAdmin) {
      const fetchMetricsData = async () => {
        setRegistrationStatsLoading(true);
        setRegistrationStatsError(null);
        setRestroomMetricsLoading(true);
        setRestroomMetricsError(null);

        try {
          const functions = getFunctions(firebaseService.app);
          
          // Fetch registration stats
          const getRegistrationStatsFn = httpsCallable(functions, 'GetRegistrationStats');
          const regResult = await getRegistrationStatsFn({
            database_id_hash: localStorage.getItem('database_id_hash') || ''
          });
          if (regResult.data.success) {
            setRegistrationStats(regResult.data);
          } else {
            setRegistrationStatsError(regResult.data.error || 'Failed to fetch registration stats.');
          }

          // Fetch restroom metrics
          const getRestroomMetricsFn = httpsCallable(functions, 'GetRestroomMetrics');
          const restroomResult = await getRestroomMetricsFn({
            database_id_hash: localStorage.getItem('database_id_hash') || ''
          });
          if (restroomResult.data.success) {
            setRestroomsAddedStats(restroomResult.data.restroomsAdded);
            setRestroomsVerifiedStats(restroomResult.data.restroomsVerified);
          } else {
            setRestroomMetricsError(restroomResult.data.error || 'Failed to fetch restroom metrics.');
          }

        } catch (error) {
          console.error('Error fetching metrics data:', error);
          setRegistrationStatsError(error.message || 'An error occurred while fetching registration stats.');
          setRestroomMetricsError(error.message || 'An error occurred while fetching restroom metrics.');
        } finally {
          setRegistrationStatsLoading(false);
          setRestroomMetricsLoading(false);
        }
      };
      fetchMetricsData();
    }
  }, [activeTab, user]);


  // Calculate stats
  useEffect(() => {
    const verifiedCount = restrooms.filter(r => r.verified).length;
    const totalReviews = restrooms.reduce((sum, r) => sum + (r.ratingCount || 0), 0);
    
    setStats({
      totalRestrooms: restrooms.length,
      verifiedRestrooms: verifiedCount,
      totalUsers: totalUserCount ?? 0,
      totalReviews
    });
  }, [restrooms, totalUserCount]);


  const handleBulkVerify = async () => {
    try {
      // Migration utilities removed for production security
      toast.error('Bulk verification disabled in production');
      return;
      
      // Original code (disabled):
      // const result = await bulkVerifyAllRestrooms();
      // if (result.success && result.updated > 0) {
      //   // Reload the page to show newly verified restrooms
      //   setTimeout(() => window.location.reload(), 2000);
      // }
    } catch (error) {
      console.error('Bulk verification error:', error);
      toast.error('Bulk verification failed');
    }
  };

  const handleDeleteRestroom = async (restroomId) => {
    if (window.confirm('Are you sure you want to delete this restroom?')) {
      try {
        await deleteRestroom(restroomId);
        toast.success('Restroom deleted successfully');
      } catch (error) {
        toast.error('Failed to delete restroom');
      }
    }
  };

  const handleVerifyRestroom = async (restroomId) => {
    try {
      await verifyRestroom(restroomId);
      
      // Track verified restroom event
      const restroom = restrooms.find(r => r.id === restroomId);
      analytics.trackVerifiedRestroom(restroomId, restroom?.name || 'Unknown', 'Admin');

      toast.success('Restroom verified successfully');
      // Remove from selected if it was selected
      setSelectedRestrooms(prev => prev.filter(id => id !== restroomId));
    } catch (error) {
      toast.error('Failed to verify restroom');
    }
  };

  const handleRunQuery = async () => {
    if (!sqlQuery.trim()) {
      toast.error('Please enter a query');
      return;
    }

    setQueryLoading(true);
    setQueryError(null);
    setQueryResults(null);

    try {
      const fireSQL = new FireSQL(firebaseService.compatDb);
      const results = await fireSQL.query(sqlQuery);
      
      // Process results to handle Timestamps before passing to ReactJson
      const processedResults = results.map(doc => {
        const processedDoc = { ...doc };
        Object.keys(processedDoc).forEach(key => {
          const value = processedDoc[key];
          if (value && typeof value === 'object' && value.seconds !== undefined && value.nanoseconds !== undefined) {
            processedDoc[key] = new Date(value.seconds * 1000).toLocaleString();
          }
        });
        return processedDoc;
      });

      setQueryResults(processedResults);
      toast.success(`Query returned ${results.length} results`);
    } catch (error) {
      console.error('FireSQL Error:', error);
      setQueryError(error.message || 'An error occurred while running the query');
      toast.error('Query failed');
    } finally {
      setQueryLoading(false);
    }
  };

  const handleStartEditRestroom = (restroom) => {
    setEditingRestroom(restroom.id);
    setEditRestroomName(restroom.name);
    setEditCategoryValue(restroom.category || '');
  };

  const handleCancelEditRestroom = () => {
    setEditingRestroom(null);
    setEditRestroomName('');
    setEditCategoryValue('');
  };

  const handleSaveRestroomName = async () => {
    if (!editRestroomName.trim()) {
      toast.error('Restroom name cannot be empty');
      return;
    }

    if (editRestroomName.trim().length < 3) {
      toast.error('Restroom name must be at least 3 characters');
      return;
    }

    try {
      setEditLoading(true);
      
      // Update both name and category
      await firebaseService.updateRestroom(editingRestroom, {
        name: editRestroomName.trim(),
        category: editCategoryValue
      });
      
      // Refresh restrooms list using DataContext's fetchRestrooms
      await fetchRestrooms();
      
      toast.success('Restroom updated successfully! ✏️');
      handleCancelEditRestroom();
    } catch (error) {
      console.error('Error updating restroom:', error);
      toast.error('Failed to update restroom');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSelectAllUnverified = () => {
    const unverifiedIds = restrooms
      .filter(r => !r.verified)
      .map(r => r.id);
    setSelectedRestrooms(unverifiedIds);
  };

  const handleDeselectAll = () => {
    setSelectedRestrooms([]);
  };

  const handleBulkVerifySelected = async () => {
    if (selectedRestrooms.length === 0) {
      toast.error('No restrooms selected');
      return;
    }

    if (!window.confirm(`Verify ${selectedRestrooms.length} selected restroom(s)?`)) {
      return;
    }

    try {
      const verifyPromises = selectedRestrooms.map(id => verifyRestroom(id));
      
      // Track verified restroom events for bulk operation
      selectedRestrooms.forEach(id => {
        const restroom = restrooms.find(r => r.id === id);
        analytics.trackVerifiedRestroom(id, restroom?.name || 'Unknown', 'Admin');
      });

      await Promise.all(verifyPromises);
      toast.success(`${selectedRestrooms.length} restroom(s) verified successfully`);
      setSelectedRestrooms([]);
      await fetchRestrooms();
    } catch (error) {
      toast.error('Failed to verify some restrooms');
    }
  };

  const handleDismissReports = async (restroomId) => {
    if (window.confirm('Are you sure you want to dismiss all reports for this restroom?')) {
      try {
        await firebaseService.updateRestroom(restroomId, { reports: [] });
        await fetchRestrooms();
        toast.success('Reports dismissed successfully');
      } catch (error) {
        console.error('Error dismissing reports:', error);
        toast.error('Failed to dismiss reports');
      }
    }
  };

  const handleViewOnMap = (restroom) => {
    // Open map in a new tab with the restroom ID
    window.open(`/app/r/${restroom.id}`, '_blank');
  };

  const handleAreYouAt = async (restroom) => {
    try {
      setMatchingLoading(true);
      setMatchingRestroom(restroom);
      const places = await firebaseService.areYouAt(restroom.latitude, restroom.longitude);
      setNearbyPlaces(places);
      setShowMatchModal(true);
    } catch (error) {
      console.error('Error in AreYouAt:', error);
      toast.error('Failed to get nearby places');
    } finally {
      setMatchingLoading(false);
    }
  };

  const handleMatchRestroom = async (place) => {
    if (!matchingRestroom) return;
    
    try {
      setMatchingLoading(true);
      await firebaseService.updateRestroom(matchingRestroom.id, {
        googlePlaceId: place.placeId,
        verified: true,
        address: place.address || matchingRestroom.address // Update address if available from Google
      });
      
      // Track verified restroom event
      analytics.trackVerifiedRestroom(matchingRestroom.id, matchingRestroom.name, 'Admin');

      toast.success(`Restroom "${matchingRestroom.name}" verified as "${place.name}"!`);
      setShowMatchModal(false);
      setMatchingRestroom(null);
      await fetchRestrooms();
    } catch (error) {
      console.error('Error matching restroom:', error);
      toast.error('Failed to verify restroom');
    } finally {
      setMatchingLoading(false);
    }
  };

  const toggleRestroomSelection = (restroomId) => {
    setSelectedRestrooms(prev => 
      prev.includes(restroomId)
        ? prev.filter(id => id !== restroomId)
        : [...prev, restroomId]
    );
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        
        await deleteUser(userId);
        // Drop from the current result set; re-run the search when one is active.
        setUsers(prev => prev.filter(u => u.id !== userId));
        if (hasSearchedUsers) await handleSearchUsers();
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleRefreshUsers = async () => {
    if (!hasSearchedUsers) {
      toast('Use the filters above and press Search to load users.');
      return;
    }
    await handleSearchUsers();
  };

  const handleSearchUsers = async (overrides = {}) => {
    const filters = { ...userFilters, ...overrides };
    setUserSearching(true);
    try {
      const functions = getFunctions(firebaseService.app);
      const searchUsers = httpsCallable(functions, 'SearchUsers');
      const { joinedAfter, joinedBefore } = getJoinedRangeBounds(filters.joinedRange);
      const call = searchUsers({
        email: filters.email,
        username: filters.username,
        name: filters.name,
        joinedAfter,
        joinedBefore,
        hasReviewed: filters.hasReviewed,
        limit: 100,
        database_id_hash: localStorage.getItem('database_id_hash') || ''
      });
      // If the Functions backend is unreachable the SDK can retry
      // indefinitely, leaving the tab stuck on "Searching…". Race a timeout
      // so the UI always resolves (same pattern as the TP rank fallback).
      call.catch(() => {});
      let searchTimer;
      const timeoutPromise = new Promise((_, reject) => {
        searchTimer = setTimeout(() => reject(new Error('search-timeout')), 12000);
      });
      let result;
      try {
        result = await Promise.race([call, timeoutPromise]);
      } finally {
        clearTimeout(searchTimer);
      }
      if (result.data?.success) {
        setUsers(result.data.users);
        setHasSearchedUsers(true);
        toast.success(`Found ${result.data.count} user${result.data.count === 1 ? '' : 's'}.`);
      } else {
        toast.error('User search failed.');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      // Don't surface raw SDK text ("internal"/"unavailable") to admins.
      toast.error(
        error?.message === 'search-timeout'
          ? 'User search failed: the request timed out.'
          : 'User search failed. Please try again.',
      );
    } finally {
      setUserSearching(false);
    }
  };

  const handleClearUserFilters = () => {
    setUserFilters({
      email: '',
      username: '',
      name: '',
      joinedRange: 'all',
      hasReviewed: 'any'
    });
    setUsers([]);
    setHasSearchedUsers(false);
  };

  const handleLoadBroadcasts = async (page = 1, append = false) => {
    setBroadcastsLoading(true);
    try {
      const functions = getFunctions(firebaseService.app);
      const getAdminBroadcasts = httpsCallable(functions, 'GetAdminBroadcasts');
      const result = await getAdminBroadcasts({
        pageNumber: page,
        pageSize: 25,
        database_id_hash: localStorage.getItem('database_id_hash') || ''
      });
      if (result.data?.success) {
        setBroadcasts(prev => append ? [...prev, ...result.data.broadcasts] : result.data.broadcasts);
        setBroadcastsPage(result.data.page);
        setBroadcastsHasMore(result.data.hasMore);
      } else {
        toast.error('Failed to load broadcast log.');
      }
    } catch (error) {
      console.error('Error loading broadcasts:', error);
      toast.error(error.message || 'Failed to load broadcast log.');
    } finally {
      setBroadcastsLoading(false);
    }
  };

  const handleToggleBroadcasts = () => {
    const next = !showBroadcasts;
    setShowBroadcasts(next);
    if (next && broadcasts.length === 0) handleLoadBroadcasts(1, false);
  };

  const handleSendAdminNotification = async () => {
    const title = notifyTitle.trim();
    const text = notifyText.trim();
    if (!title) {
      toast.error('Enter a notification title.');
      return;
    }
    if (!text) {
      toast.error('Enter notification text.');
      return;
    }
    const recipientCount = notifySendToAll ? 'all users' : `${users.length} user${users.length === 1 ? '' : 's'}`;
    const channelLabel = notifyChannels === 'both' ? 'inbox + phone notification' : notifyChannels === 'push' ? 'phone notification' : 'in-app inbox';
    if (!window.confirm(`Send this ${channelLabel} to ${recipientCount}?\n\n${title}\n${text}`)) {
      return;
    }
    setNotifySending(true);
    try {
      const functions = getFunctions(firebaseService.app);
      const sendAdminNotification = httpsCallable(functions, 'SendAdminNotification');
      const result = await sendAdminNotification({
        userIds: notifySendToAll ? [] : users.map(u => u.id),
        sendToAll: notifySendToAll,
        title,
        text,
        channels: notifyChannels === 'both' ? ['inbox', 'push'] : [notifyChannels],
        database_id_hash: localStorage.getItem('database_id_hash') || ''
      });
      if (result.data?.success) {
        const parts = [];
        if (result.data.inboxWritten) parts.push(`inbox: ${result.data.inboxWritten}`);
        if (result.data.pushSent || result.data.pushSkipped) {
          parts.push(`push: ${result.data.pushSent} sent, ${result.data.pushSkipped} no device`);
        }
        if (result.data.failureCount) parts.push(`${result.data.failureCount} failed`);
        toast.success(`Sent to ${result.data.recipientCount} user${result.data.recipientCount === 1 ? '' : 's'} (${parts.join('; ') || 'no channels'}).`);
        setNotifyTitle('');
        setNotifyText('');
        setShowNotifyModal(false);
        if (showBroadcasts) handleLoadBroadcasts(1, false);
      } else {
        toast.error('Broadcast failed.');
      }
    } catch (error) {
      console.error('Error sending admin notification:', error);
      toast.error(error.message || 'Broadcast failed.');
    } finally {
      setNotifySending(false);
    }
  };

  const handleFixGeohashes = async () => {
    if (window.confirm('This will scan all restrooms and populate missing geohashes based on their latitude/longitude. Continue?')) {
      try {
        setMigrationLoading(true);
        const functions = getFunctions(firebaseService.app);
        const fixGeohashesFn = httpsCallable(functions, 'Migration_FixMissingGeohashes');
        const result = await fixGeohashesFn({
          database_id_hash: localStorage.getItem('database_id_hash') || ''
        });
        
        if (result.data.success) {
          toast.success(`Successfully fixed ${result.data.fixedCount} restrooms! 🎉`);
          await fetchRestrooms();
        } else {
          toast.error(`Fix failed: ${result.data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error fixing geohashes:', error);
        toast.error(`Error: ${error.message}`);
      } finally {
        setMigrationLoading(false);
      }
    }
  };

  // Admin-only: trigger immediate rebuild of TP leaderboards and distribution cache
  const handleRefreshTpLeaderboardsNow = async () => {
    try {
      setRefreshTpLoading(true);
      const functions = getFunctions(firebaseService.app);
      const refreshFn = httpsCallable(functions, 'RefreshTPLeaderboardsNow');
      const result = await refreshFn({
        database_id_hash: localStorage.getItem('database_id_hash') || ''
      });

      if (result?.data?.success) {
        setLastRefreshInfo({ time: new Date(), docs: result.data.updatedDocs || [] });
        toast.success('TP leaderboards refreshed successfully.');
      } else {
        toast.error(result?.data?.message || 'Refresh failed.');
      }
    } catch (error) {
      console.error('Error refreshing TP leaderboards:', error);
      toast.error(error.message || 'Failed to refresh TP leaderboards');
    } finally {
      setRefreshTpLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, username, currentAdminStatus) => {
    const action = currentAdminStatus ? 'remove admin powers from' : 'make admin';
    if (window.confirm(`Are you sure you want to ${action} ${username}?`)) {
      try {
        // Prevent admin from removing their own admin status
        if (userId === user?.id && currentAdminStatus) {
          toast.error('You cannot remove your own admin powers!');
          return;
        }

        // Update user's admin status in Firebase
        await firebaseService.updateUserAdminStatus(userId, !currentAdminStatus);
        
        // Update the row in the current result set (no full re-download)
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, isAdmin: !currentAdminStatus } : u));
        
        const newStatus = !currentAdminStatus;
        toast.success(`${username} is now ${newStatus ? 'an admin 👑' : 'a regular user 👤'}`);
        
        // If current user was made admin, refresh their session
        if (userId === user?.id && newStatus) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Error toggling admin status:', error);
        toast.error('Failed to update admin status');
      }
    }
  };

  // User document migration functions - commented out since all users are migrated
  /*
  const handleCheckUserDocMigration = async () => {
    try {
      const status = await checkUserDocMigration();
      setUserDocMigrationStatus(status);
      console.log('📊 User document migration status:', status);
    } catch (error) {
      console.error('Error checking user document migration status:', error);
      toast.error('Failed to check migration status');
    }
  };

  const handleMigrateUserDocuments = async () => {
    if (window.confirm('This will migrate all user documents to use Firebase UID as document ID. Continue?')) {
      try {
        setMigrationLoading(true);
        const result = await migrateAllUsers();
        setMigrationResults([result]);
        
        if (result.success) {
          toast.success(`Successfully migrated ${result.migrated}/${result.total} users! 🎉`);
          // Refresh users list
          const usersData = await firebaseService.getUsers();
          setUsers(usersData);
        } else {
          toast.error(`Migration failed: ${result.error}`);
          setMigrationResults([result]);
        }
      } catch (error) {
        toast.error(`Error: ${error.message}`);
      } finally {
        setMigrationLoading(false);
      }
    }
  };

  const handleMigrateIndividualUser = async (userId, username) => {
    if (window.confirm(`Migrate user "${username}" (${userId}) to use Firebase UID as document ID?`)) {
      try {
        setMigrationLoading(true);
        const result = await migrateUserDocumentId(userId);
        
        if (result.success) {
          toast.success(`Successfully migrated ${username}! 🎉`);
          // Refresh users list and migration status
          const usersData = await firebaseService.getUsers();
          setUsers(usersData);
          if (userDocMigrationStatus) {
            handleCheckUserDocMigration();
          }
        } else {
          toast.error(`Migration failed: ${result.error}`);
        }
      } catch (error) {
        toast.error(`Error: ${error.message}`);
      } finally {
        setMigrationLoading(false);
      }
    }
  };

  // Helper function to check if a user needs migration
  const userNeedsMigration = (user) => {
    return user.id !== user.firebaseUid && user.firebaseUid && user.firebaseUid !== '';
  };
  */

  // Review ID Migration functions (disabled for production security)
  const handleCheckReviewIdMigration = async () => {
    // Migration utilities removed for production security
    toast.error('Review migration disabled in production');
    return;
    
    // Original code (disabled):
    // try {
    //   setReviewIdMigrationLoading(true);
    //   const status = await checkMigrationNeeded();
    //   setReviewIdMigrationStatus(status);
    //   console.log('📊 Review ID migration status:', status);
    //   
    //   if (status.migrationNeeded) {
    //     toast(`⚠️ Found ${status.reviewsWithoutIds} reviews without proper IDs`, {
    //       icon: '⚠️',
    //       style: {
    //         background: '#fef3c7',
    //         color: '#92400e',
    //         border: '1px solid #fbbf24'
    //       }
    //     });
    //   } else {
    //     toast.success('✅ All reviews have proper IDs - no migration needed');
    //   }
    // } catch (error) {
    //   console.error('Error checking review ID migration status:', error);
    //   toast.error('Failed to check migration status: ' + error.message);
    // } finally {
    //   setReviewIdMigrationLoading(false);
    // }
  };

  const handleMigrateReviewIds = async () => {
    // Migration utilities removed for production security
    toast.error('Review migration disabled in production');
    return;
    
    // Original code (disabled):
    // const status = await checkMigrationNeeded();
    // setReviewIdMigrationStatus(status);
    // console.log('📊 Review ID migration status:', status);
    // 
    // if (status.migrationNeeded) {
    //   toast(`⚠️ Found ${status.reviewsWithoutIds} reviews without proper IDs`, {
    //     icon: '⚠️',
    //     style: {
    //       background: '#fef3c7',
    //       color: '#92400e',
    //       border: '1px solid #fbbf24'
    //     }
    //   });
    // } else {
    //   toast.success('✅ All reviews have proper IDs - no migration needed');
    // }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      // Handle Firestore Timestamp
      date = dateValue.toDate();
    } else if (dateValue.seconds !== undefined) {
      // Handle timestamp-like object
      date = new Date(dateValue.seconds * 1000);
    } else {
      // Handle string or Date object
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getRatingEmoji = (rating) => {
    const fullEmojis = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const emptyEmojis = 5 - fullEmojis - (hasHalf ? 1 : 0);
    
    return (
      <>
        {Array(fullEmojis).fill(0).map((_, index) => (
          <span key={`full-${index}`} style={{ opacity: 1 }}>💩</span>
        ))}
        {hasHalf && (
          <span key="half" style={{ opacity: 0.6 }}>💩</span>
        )}
        {Array(emptyEmojis).fill(0).map((_, index) => (
          <span key={`empty-${index}`} style={{ opacity: 0.3 }}>💩</span>
        ))}
      </>
    );
  };

  const handleApplyFilter = () => {
    setAppliedSearch(restroomSearch);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilter();
    }
  };

  const handleRestroomSort = (column) => {
    setRestroomSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleUserSort = (column) => {
    setUserSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortRestrooms = (restroomsToSort) => {
    return [...restroomsToSort].sort((a, b) => {
      let aVal, bVal;
      
      switch (restroomSort.column) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'category':
          aVal = a.category || '';
          bVal = b.category || '';
          break;
        case 'rating':
          aVal = a.averageRating || a.rating || 0;
          bVal = b.averageRating || b.rating || 0;
          break;
        case 'reviews':
          aVal = a.ratingCount || 0;
          bVal = b.ratingCount || 0;
          break;
        case 'status':
          aVal = a.verified ? 1 : 0;
          bVal = b.verified ? 1 : 0;
          break;
        case 'createdBy':
          aVal = a.createdByUsername?.toLowerCase() || '';
          bVal = b.createdByUsername?.toLowerCase() || '';
          break;
        case 'date':
          const getTimestamp = (val) => {
            if (!val) return 0;
            if (val.toMillis) return val.toMillis();
            if (val.seconds !== undefined) return val.seconds * 1000;
            return new Date(val).getTime() || 0;
          };
          aVal = getTimestamp(a.createdAt || a.date);
          bVal = getTimestamp(b.createdAt || b.date);
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return restroomSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return restroomSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortUsers = (usersToSort) => {
    return [...usersToSort].sort((a, b) => {
      let aVal, bVal;
      
      switch (userSort.column) {
        case 'name':
          aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
          bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'username':
          aVal = a.username?.toLowerCase() || '';
          bVal = b.username?.toLowerCase() || '';
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'admin':
          aVal = a.isAdmin ? 1 : 0;
          bVal = b.isAdmin ? 1 : 0;
          break;
        case 'date':
          const getUserTimestamp = (val) => {
            if (!val) return 0;
            if (val.toMillis) return val.toMillis();
            if (val.seconds !== undefined) return val.seconds * 1000;
            return new Date(val).getTime() || 0;
          };
          aVal = getUserTimestamp(a.createdAt || a.joinedDate);
          bVal = getUserTimestamp(b.createdAt || b.joinedDate);
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return userSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return userSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (column, sortState) => {
    if (sortState.column !== column) return ' ⇅';
    return sortState.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Function to convert JSON to CSV
  const convertToCSV = (data) => {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [];

    // Add header row
    csvRows.push(headers.join(','));

    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const escaped = ('' + row[header]).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  // Function to handle CSV download
  const handleDownloadReviewsCSV = async () => {
    try {
      toast.loading('Fetching reviews for CSV...', { id: 'csv-download' });
      const reviewsData = await firebaseService.getAllReviewsForExport();

      if (reviewsData.length === 0) {
        toast.error('No reviews found to export.', { id: 'csv-download' });
        return;
      }

      const csv = convertToCSV(reviewsData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'crappermapper_reviews.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast.success('Reviews CSV downloaded successfully!', { id: 'csv-download' });
    } catch (error) {
      console.error('Error downloading reviews CSV:', error);
      toast.error('Failed to download reviews CSV.', { id: 'csv-download' });
    }
  };

  // Migration handlers - COMMENTED OUT since migration is complete
  /*
  const handleCheckStatus = async () => {
    if (!migrationUsername.trim()) {
      toast.error('Please enter a username');
      return;
    }

    setMigrationLoading(true);
    try {
      const status = await checkMigrationStatus(migrationUsername.trim());
      setMigrationStatus(status);
      
      if (status.exists) {
        if (status.isMigrated) {
          toast.success(`${migrationUsername} is migrated ✅`);
        } else {
          toast(`${migrationUsername} needs migration`, { icon: '⚠️' });
        }
      } else {
        toast.error('User not found');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleSingleMigration = async () => {
    // Migration utilities removed for production security
    toast.error('User migration disabled in production');
    return;
    
    if (!migrationUsername.trim()) {
      toast.error('Please enter a username');
      return;
    }

    setMigrationLoading(true);
    setMigrationResults([]);

    try {
      const result = await migrateLegacyAccount(migrationUsername.trim());
      
      if (result.success) {
        if (result.alreadyMigrated) {
          toast.success(`${migrationUsername} already migrated!`);
        } else {
          toast.success(`${migrationUsername} migrated successfully! 🎉`);
        }
        setMigrationResults([result]);
        // Refresh users list
        const usersData = await firebaseService.getUsers();
        setUsers(usersData);
      } else {
        toast.error(`Migration failed: ${result.message}`);
        setMigrationResults([result]);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleBatchMigration = async () => {
    // Migration utilities removed for production security
    toast.error('Batch migration disabled in production');
    return;
    
    if (!migrationBatch.trim()) {
      toast.error('Please enter usernames');
      return;
    }

    setMigrationLoading(true);
    setMigrationResults([]);

    try {
      const usernames = migrationBatch
        .split('\n')
        .map(u => u.trim())
        .filter(u => u.length > 0);

      if (usernames.length === 0) {
        toast.error('No valid usernames found');
        setMigrationLoading(false);
        return;
      }

      toast.loading(`Migrating ${usernames.length} accounts...`);
      const batchResults = await migrateLegacyAccountsBatch(usernames);
      
      toast.dismiss();
      toast.success(`Migration complete! ${batchResults.successful.length} migrated, ${batchResults.failed.length} failed`);
      
      setMigrationResults([{
        type: 'batch',
        ...batchResults
      }]);
      
      // Refresh users list
      const usersData = await firebaseService.getUsers();
      setUsers(usersData);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setMigrationLoading(false);
    }
  };
  */

  if (!user?.isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>You don't have permission to access the admin dashboard.</p>
          <button className="btn btn-primary" onClick={() => window.location.href = '/'}>
            Go Back to Map
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="header-left">
          <h1>🛠️ Admin Dashboard</h1>
          <p>CrapperMapper Administration</p>
        </div>
        <div className="header-right">
          <button 
            className="btn btn-secondary"
            onClick={() => window.location.href = '/'}
          >
            🗺️ Back to Map
          </button>
          <button className="btn btn-danger" onClick={logout}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="admin-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'restrooms' ? 'active' : ''}`}
          onClick={() => setActiveTab('restrooms')}
        >
          🚽 Restrooms ({restrooms.length})
        </button>
        <button 
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({users.length})
        </button>
        <button 
          className={`nav-tab ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          📈 Metrics
        </button>
        <button 
          className="nav-tab"
          onClick={() => setActiveTab('reports')}
        >
          🚨 Reports
        </button>
        <button 
          className={`nav-tab ${activeTab === 'qr-management' ? 'active' : ''}`}
          onClick={() => setActiveTab('qr-management')}
        >
          📱 QR Management
        </button>
        <button 
          className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
        <button 
          className={`nav-tab ${activeTab === 'gameconfig' ? 'active' : ''}`}
          onClick={() => setActiveTab('gameconfig')}
        >
          🎮 Game Config
        </button>
        <button 
          className={`nav-tab ${activeTab === 'queries' ? 'active' : ''}`}
          onClick={() => setActiveTab('queries')}
        >
          🔍 Queries
        </button>
        {/* Migration tab button commented out since migration is complete
        <button 
          className={`nav-tab ${activeTab === 'migration' ? 'active' : ''}`}
          onClick={() => setActiveTab('migration')}
        >
          🔐 Migration
        </button>
        */}
      </div>

      {/* Content Area */}
      <div className="admin-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">🚽</div>
                <div className="stat-info">
                  <h3>{stats.totalRestrooms}</h3>
                  <p>Total Restrooms</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <h3>{stats.verifiedRestrooms}</h3>
                  <p>Verified Restrooms</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <h3>{stats.totalUsers}</h3>
                  <p>Total Users</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">⭐</div>
                <div className="stat-info">
                  <h3>{stats.totalReviews}</h3>
                  <p>Total Reviews</p>
                </div>
              </div>
            </div>

            <div className="recent-activity">
              <h2>Recent Activity</h2>
              <div className="activity-list">
                {(() => {
                  // Combine users, restrooms, and reviews with type indicator
                  const userActivities = recentUsers.map(user => ({
                    id: `user-${user.id}`,
                    type: 'user',
                    username: user.username,
                    name: `${user.firstName} ${user.lastName}`,
                    createdAt: user.createdAt || user.joinedDate, // Use createdAt or joinedDate fallback
                    icon: '👤'
                  }));
                  
                  const restroomActivities = restrooms.map(restroom => ({
                    id: `restroom-${restroom.id}`,
                    type: 'restroom',
                    username: restroom.createdByUsername,
                    name: restroom.name,
                    createdAt: restroom.createdAt || restroom.date,
                    icon: '🚽'
                  }));
                  
                  // Extract all reviews from all restrooms
                  const reviewActivities = restrooms.flatMap(restroom => 
                    (restroom.reviews || []).map(review => ({
                      id: `review-${review.id || review.createdAt || review.date}-${restroom.id}`,
                      type: 'review',
                      username: review.username,
                      restroomName: restroom.name,
                      rating: review.rating,
                      createdAt: review.createdAt || review.date, // Use createdAt or date fallback
                      icon: '⭐'
                    }))
                  );
                  
                  // Combine and sort by date descending (newest first)
                  const allActivities = [...userActivities, ...restroomActivities, ...reviewActivities]
                    .sort((a, b) => {
                      const getTS = (v) => {
                        if (!v) return 0;
                        if (v.toMillis) return v.toMillis();
                        if (v.seconds !== undefined) return v.seconds * 1000;
                        return new Date(v).getTime() || 0;
                      };
                      return getTS(b.createdAt) - getTS(a.createdAt);
                    })
                    .slice(0, 15); // Show top 15 most recent
                  
                  return allActivities.map(activity => (
                    <div key={activity.id} className="activity-item">
                      <div className="activity-icon">{activity.icon}</div>
                      <div className="activity-info">
                        {activity.type === 'user' ? (
                          <p><strong>{activity.name}</strong> (@{activity.username}) joined</p>
                        ) : activity.type === 'review' ? (
                          <p><strong>{activity.username}</strong> reviewed "{activity.restroomName}" {'💩'.repeat(Math.round(activity.rating))}</p>
                        ) : (
                          <p><strong>{activity.username}</strong> added "{activity.name}"</p>
                        )}
                        <span className="activity-date">{formatDate(activity.createdAt)}</span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'restrooms' && (
          <div className="restrooms-tab">
            <div className="tab-header">
              <h2>Manage Restrooms</h2>
              <div className="filters">
                <div className="search-group">
                  <input 
                    type="text" 
                    className="search-input" 
                    placeholder="Filter restrooms..." 
                    value={restroomSearch}
                    onChange={(e) => setRestroomSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <button 
                    className="btn btn-secondary filter-btn"
                    onClick={handleApplyFilter}
                  >
                    🔍 Filter
                  </button>
                </div>
                <select value={restroomFilter} onChange={(e) => setRestroomFilter(e.target.value)}>
                  <option value="all">All Restrooms ({restrooms.length})</option>
                  <option value="verified">Verified Only ({restrooms.filter(r => !r.verified).length})</option>
                  <option value="unverified">Unverified Only ({restrooms.filter(r => !r.verified).length})</option>
                </select>
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleDownloadReviewsCSV}
              >
                ⬇️ Download Reviews CSV
              </button>
            </div>

            {/* Bulk Actions Toolbar */}
            {restrooms.filter(r => !r.verified).length > 0 && (
              <div className="bulk-actions-toolbar">
                <div className="bulk-actions-left">
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={handleSelectAllUnverified}
                  >
                    Select All Unverified ({restrooms.filter(r => !r.verified).length})
                  </button>
                  {selectedRestrooms.length > 0 && (
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={handleDeselectAll}
                    >
                      Deselect All
                    </button>
                  )}
                </div>
                {selectedRestrooms.length > 0 && (
                  <div className="bulk-actions-right">
                    <span className="selection-count">{selectedRestrooms.length} selected</span>
                    <button 
                      className="btn btn-sm btn-success"
                      onClick={handleBulkVerifySelected}
                    >
                      ✅ Verify Selected ({selectedRestrooms.length})
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="restrooms-table">
              <table>
                <thead>
                  <tr>
                    <th style={{width: '30px'}}></th>
                    <th onClick={() => handleRestroomSort('name')} className="sortable">
                      Name{getSortIcon('name', restroomSort)}
                    </th>
                    <th onClick={() => handleRestroomSort('category')} className="sortable">
                      Category{getSortIcon('category', restroomSort)}
                    </th>
                    <th onClick={() => handleRestroomSort('rating')} className="sortable">
                      Rating{getSortIcon('rating', restroomSort)}
                    </th>
                    <th onClick={() => handleRestroomSort('reviews')} className="sortable">
                      Reviews{getSortIcon('reviews', restroomSort)}
                    </th>
                    <th onClick={() => handleRestroomSort('status')} className="sortable">
                      Status{getSortIcon('status', restroomSort)}
                    </th>
                    <th onClick={() => handleRestroomSort('createdBy')} className="sortable">
                      Created By{getSortIcon('createdBy', restroomSort)}
                    </th>
                    <th onClick={() => handleRestroomSort('date')} className="sortable">
                      Date{getSortIcon('date', restroomSort)}
                    </th>
                    <th style={{width: '120px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortRestrooms(
                    restrooms.filter(restroom => {
                      // Status Filter
                      const matchesStatus = restroomFilter === 'all' || 
                        (restroomFilter === 'verified' && restroom.verified === true) ||
                        (restroomFilter === 'unverified' && !restroom.verified);
                      
                      if (!matchesStatus) return false;

                      // Text Filter (Any Field)
                      if (!appliedSearch.trim()) return true;

                      const searchLower = appliedSearch.toLowerCase();
                      return (
                        (restroom.name && restroom.name.toLowerCase().includes(searchLower)) ||
                        (restroom.category && restroom.category.toLowerCase().includes(searchLower)) ||
                        (restroom.address && restroom.address.toLowerCase().includes(searchLower)) ||
                        (restroom.createdBy && restroom.createdBy.toLowerCase().includes(searchLower)) ||
                        (restroom.id && restroom.id.toLowerCase().includes(searchLower))
                      );
                    })
                  ).map(restroom => (
                    <tr key={restroom.id} className={selectedRestrooms.includes(restroom.id) ? 'selected-row' : ''}>
                      <td>
                        {!restroom.verified && (
                          <input
                            type="checkbox"
                            checked={selectedRestrooms.includes(restroom.id)}
                            onChange={() => toggleRestroomSelection(restroom.id)}
                          />
                        )}
                      </td>
                      <td className="restroom-name">
                        {editingRestroom === restroom.id ? (
                          <input
                            type="text"
                            value={editRestroomName}
                            onChange={(e) => setEditRestroomName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.focus()}
                            className="form-control form-control-sm"
                            placeholder="Restroom name"
                            disabled={editLoading}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveRestroomName();
                              if (e.key === 'Escape') handleCancelEditRestroom();
                            }}
                          />
                        ) : (
                          <span>{restroom.name}</span>
                        )}
                      </td>
                      <td>
                        {editingRestroom === restroom.id ? (
                          <select
                            value={editCategoryValue}
                            onChange={(e) => setEditCategoryValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.focus()}
                            className="form-control form-control-sm"
                            disabled={editLoading}
                          >
                            <option value="gas-station">⛽ Gas Station</option>
                            <option value="truck-stop">🚛 Truck Stop</option>

                            <option value="restaurant">🍔 Restaurant</option>
                            <option value="bar">🍺 Bar/Nightclub</option>
                            <option value="coffee-shop">☕ Coffee Shop</option>
                            <option value="park">🌳 Park/Recreation</option>
                            <option value="transit">🚇 Transit Station</option>
                            <option value="retail">🛍️ Retail Store</option>
                            <option value="office">🏢 Office Building</option>
                            <option value="public-facility">🏛️ Public Facility</option>
                            <option value="hotel">🏨 Hotel/Motel</option>
                            <option value="other">📍 Other</option>
                          </select>
                        ) : (
                          <span>{restroom.category?.replace('-', ' ') || 'Unknown'}</span>
                        )}
                      </td>
                      <td>
                        <div className="rating-cell">
                          {getRatingEmoji(restroom.averageRating || restroom.rating)}
                          <span>({(restroom.averageRating || restroom.rating).toFixed(1)})</span>
                        </div>
                      </td>
                      <td>{restroom.ratingCount}</td>
                      <td>
                        <span className={`status-badge ${restroom.verified ? 'verified' : 'unverified'}`}>
                          {restroom.verified ? 'Verified' : 'Unverified'}
                        </span>
                      </td>
                      <td>{restroom.createdByUsername}</td>
                      <td>{formatDate(restroom.createdAt || restroom.date)}</td>
                      <td>
                        <div className="action-buttons">
                          {editingRestroom === restroom.id ? (
                            <>
                              <button 
                                className="btn btn-sm btn-success"
                                onClick={handleSaveRestroomName}
                                disabled={editLoading}
                              >
                                💾 Save
                              </button>
                              <button 
                                className="btn btn-sm btn-secondary"
                                onClick={handleCancelEditRestroom}
                                disabled={editLoading}
                              >
                                ❌ Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              {!restroom.verified && (
                                <button 
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleVerifyRestroom(restroom.id)}
                                >
                                  ✅ Verify
                                </button>
                              )}
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => handleStartEditRestroom(restroom)}
                                title="Edit restroom name and category"
                              >
                                ✏️ Edit
                              </button>
                            </>
                          )}
                          <button 
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewOnMap(restroom)}
                            title="View restroom on map"
                          >
                            🗺️ Map
                          </button>
                          {!restroom.verified && (
                            <button 
                              className="btn btn-sm btn-warning"
                              onClick={() => handleAreYouAt(restroom)}
                              disabled={matchingLoading}
                              title="Verify using Google Places"
                            >
                              🔍 AreYouAt
                            </button>
                          )}
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRestroom(restroom.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-tab">
            <div className="tab-header">
              <h2>Manage Users</h2>
            </div>

            <div className="filters users-filter-bar">
              <input
                className="search-input"
                type="text"
                placeholder="Email"
                value={userFilters.email}
                onChange={(e) => setUserFilters(prev => ({ ...prev, email: e.target.value }))}
              />
              <input
                className="search-input"
                type="text"
                placeholder="Username"
                value={userFilters.username}
                onChange={(e) => setUserFilters(prev => ({ ...prev, username: e.target.value }))}
              />
              <input
                className="search-input"
                type="text"
                placeholder="Name"
                value={userFilters.name}
                onChange={(e) => setUserFilters(prev => ({ ...prev, name: e.target.value }))}
              />
              <label className="users-filter-date">
                <span>Joined</span>
                <select
                  value={userFilters.joinedRange}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, joinedRange: e.target.value }))}
                >
                  {USER_JOINED_RANGES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <select
                value={userFilters.hasReviewed}
                onChange={(e) => setUserFilters(prev => ({ ...prev, hasReviewed: e.target.value }))}
                title="Created a review"
              >
                <option value="any">Reviewed: Any</option>
                <option value="yes">Reviewed: Yes</option>
                <option value="no">Reviewed: No</option>
              </select>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleSearchUsers()}
                disabled={userSearching}
              >
                {userSearching ? 'Searching…' : '🔍 Search'}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleClearUserFilters}
                disabled={userSearching}
              >
                Clear
              </button>
            </div>

            {hasSearchedUsers && (
              <div className="users-result-count">
                {users.length} user{users.length === 1 ? '' : 's'} found
              </div>
            )}

            {!hasSearchedUsers ? (
              <div className="users-empty-state">
                <p>No users loaded. Use the filters above and press Search.</p>
              </div>
            ) : users.length === 0 ? (
              <div className="users-empty-state">
                <p>No users match these filters.</p>
              </div>
            ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleUserSort('name')} className="sortable">
                      Name{getSortIcon('name', userSort)}
                    </th>
                    <th onClick={() => handleUserSort('username')} className="sortable">
                      Username{getSortIcon('username', userSort)}
                    </th>
                    <th onClick={() => handleUserSort('email')} className="sortable">
                      Email{getSortIcon('email', userSort)}
                    </th>
                    <th>Phone</th>
                    <th onClick={() => handleUserSort('admin')} className="sortable">
                      Is Admin{getSortIcon('admin', userSort)}
                    </th>
                    {/* Migration column commented out since all users are migrated
                    <th>Migration</th>
                    */}
                    <th onClick={() => handleUserSort('date')} className="sortable">
                      Joined{getSortIcon('date', userSort)}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortUsers(users).map(userData => (
                    <tr key={userData.id}>
                      <td>{userData.firstName} {userData.lastName}</td>
                      <td>@{userData.username}</td>
                      <td>{userData.email}</td>
                      <td>{userData.phone}</td>
                      <td>
                        <div className="admin-status-cell">
                          <span className={`admin-badge ${userData.isAdmin ? 'admin' : 'user'}`}>
                            {userData.isAdmin ? '👑 Admin' : '👤 User'}
                          </span>
                          <button 
                            className="btn btn-sm btn-outline-secondary toggle-admin-btn"
                            onClick={() => handleToggleAdmin(userData.id, userData.username, userData.isAdmin)}
                            title={`${userData.isAdmin ? 'Remove admin powers from' : 'Make admin'} ${userData.username}`}
                          >
                            {userData.isAdmin ? '👎 Remove' : '👍 Make Admin'}
                          </button>
                        </div>
                      </td>
                      {/* Migration status cell commented out since all users are migrated
                      <td>
                        {userNeedsMigration(userData) ? (
                          <div className="migration-status-cell">
                            <span className="migration-badge needed">Needs Migration</span>
                            <button 
                              className="btn btn-sm btn-primary migrate-btn"
                              onClick={() => handleMigrateIndividualUser(userData.id, userData.username)}
                              disabled={migrationLoading}
                              title={`Migrate ${userData.username} from ${userData.id} to ${userData.firebaseUid}`}
                            >
                              🔄 Migrate
                            </button>
                          </div>
                        ) : (
                          <span className="migration-badge ok">✅ Migrated</span>
                        )}
                      </td>
                      */}
                      <td>{formatDate(userData.createdAt || userData.joinedDate)}</td>
                      <td>
                        <div className="action-buttons">
                          {/* Individual migration button commented out since all users are migrated
                          {userNeedsMigration(userData) && (
                            <button 
                              className="btn btn-sm btn-primary migrate-btn"
                              onClick={() => handleMigrateIndividualUser(userData.id, userData.username)}
                              disabled={migrationLoading}
                              title={`Migrate ${userData.username}`}
                            >
                              🔄 Migrate
                            </button>
                          )}
                          */}
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteUser(userData.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}


            <div className="users-tab-actions">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => setShowNotifyModal(true)}
              >
                📣 Notify users
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleToggleBroadcasts}
              >
                {showBroadcasts ? '▾ Hide broadcast log' : '▸ Show broadcast log'}
              </button>
            </div>

            <div className="broadcast-log">
              {showBroadcasts && (
                broadcastsLoading && broadcasts.length === 0 ? (
                  <p>Loading broadcast log…</p>
                ) : broadcasts.length === 0 ? (
                  <div className="users-empty-state">
                    <p>No broadcasts sent yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="users-table broadcasts-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sent</th>
                            <th>By</th>
                            <th>Title</th>
                            <th>Text</th>
                            <th>Channels</th>
                            <th>Recipients</th>
                            <th>Delivered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {broadcasts.map(b => (
                            <tr key={b.id}>
                              <td>{b.sentAt ? new Date(b.sentAt).toLocaleString() : '—'}</td>
                              <td>{b.sentByUsername || b.sentByUid || '—'}</td>
                              <td>{b.title}</td>
                              <td title={b.text}>
                                {b.text && b.text.length > 80 ? `${b.text.slice(0, 80)}…` : b.text}
                              </td>
                              <td>{(b.channels || []).join(' + ')}</td>
                              <td>
                                {b.recipientCount}{b.scope === 'all-users' ? ' (all)' : ''}
                              </td>
                              <td>
                                inbox: {b.inboxWritten ?? 0}
                                {b.pushSent || b.pushSkipped ? `; push: ${b.pushSent ?? 0} sent, ${b.pushSkipped ?? 0} no device` : ''}
                                {b.failureCount ? `; ${b.failureCount} failed` : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {broadcastsHasMore && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleLoadBroadcasts(broadcastsPage + 1, true)}
                        disabled={broadcastsLoading}
                      >
                        {broadcastsLoading ? 'Loading…' : 'Load more'}
                      </button>
                    )}
                  </>
                )
              )}
            </div>
          </div>
        )}

        {showNotifyModal && (
          <div className="notify-modal-overlay" onClick={() => setShowNotifyModal(false)}>
            <div className="notify-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="notify-modal-close"
                onClick={() => setShowNotifyModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="notify-composer">
                <h4>📣 Notify users</h4>
                <div className="notify-recipients">
                  {notifySendToAll ? (
                    <span>Recipients: <strong>all users</strong></span>
                  ) : (
                    <span>Recipients: <strong>{users.length} listed user{users.length === 1 ? '' : 's'}</strong></span>
                  )}
                  <label className="notify-send-all">
                    <input
                      type="checkbox"
                      checked={notifySendToAll}
                      onChange={(e) => setNotifySendToAll(e.target.checked)}
                    />
                    Send to all users
                  </label>
                </div>
                <input
                  className="search-input notify-title-input"
                  type="text"
                  placeholder="Notification title (required)"
                  maxLength={80}
                  value={notifyTitle}
                  onChange={(e) => setNotifyTitle(e.target.value)}
                />
                <textarea
                  className="notify-text-input"
                  placeholder="Notification text (required, max 200 characters)"
                  maxLength={200}
                  rows={3}
                  value={notifyText}
                  onChange={(e) => setNotifyText(e.target.value)}
                />
                <div className="notify-options-row">
                  <div className="notify-channels" role="radiogroup" aria-label="Notification channels">
                    <label>
                      <input
                        type="radio"
                        name="notify-channel"
                        value="inbox"
                        checked={notifyChannels === 'inbox'}
                        onChange={() => setNotifyChannels('inbox')}
                      />
                      In-app inbox only
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="notify-channel"
                        value="push"
                        checked={notifyChannels === 'push'}
                        onChange={() => setNotifyChannels('push')}
                      />
                      Phone notification only
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="notify-channel"
                        value="both"
                        checked={notifyChannels === 'both'}
                        onChange={() => setNotifyChannels('both')}
                      />
                      Both
                    </label>
                  </div>
                  <span className="notify-char-count">{notifyText.trim().length}/200</span>
                </div>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleSendAdminNotification}
                  disabled={notifySending || (!notifySendToAll && users.length === 0)}
                  title={!notifySendToAll && users.length === 0 ? 'Search for users first, or tick "Send to all users"' : 'Send notification'}
                >
                  {notifySending ? 'Sending…' : '📤 Send notification'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div className="metrics-tab">
            <div className="tab-header">
              <h2>📈 Metrics</h2>
            </div>

            <div className="metric-section">
              <h3>New User Registrations (Last 7 Days)</h3>
              {registrationStatsLoading && <p>Loading registration stats...</p>}
              {registrationStatsError && <p className="error-message">Error: {registrationStatsError}</p>}
              {registrationStats && (
                <div className="chart-container">
                  <Line data={registrationStats} options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'New User Registrations by Source',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Registrations'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Date'
                        }
                      }
                    }
                  }} />
                </div>
              )}
            </div>

            <div className="metric-section">
              <h3>Restrooms Added (Last 7 Days)</h3>
              {restroomMetricsLoading && <p>Loading restroom added stats...</p>}
              {restroomMetricsError && <p className="error-message">Error: {restroomMetricsError}</p>}
              {restroomsAddedStats && (
                <div className="chart-container">
                  <Line data={restroomsAddedStats} options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Restrooms Added by Google Place ID Presence',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Restrooms Added'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Date'
                        }
                      }
                    }
                  }} />
                </div>
              )}
            </div>

            <div className="metric-section">
              <h3>Restrooms Verified (Last 7 Days)</h3>
              {restroomMetricsLoading && <p>Loading restroom verification stats...</p>}
              {restroomMetricsError && <p className="error-message">Error: {restroomMetricsError}</p>}
              {restroomsVerifiedStats && (
                <div className="chart-container">
                  <Line data={restroomsVerifiedStats} options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Restrooms Verified by Method',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Restrooms Verified'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Date'
                        }
                      }
                    }
                  }} />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="reports-tab">
            <div className="tab-header">
              <h2>🚨 Reports</h2>
            </div>

            <div className="report-section">
              <h3>Reported Content</h3>
              <div className="reports-list">
                {restrooms.filter(r => r.reports && r.reports.length > 0).length > 0 ? (
                  restrooms.filter(r => r.reports && r.reports.length > 0).map(restroom => (
                    <div key={restroom.id} className="report-item">
                      <div className="report-header">
                        <h3>{restroom.name}</h3>
                        <span className="report-count">{restroom.reports.length} reports</span>
                      </div>
                      <div className="report-details">
                        {restroom.reports.map(report => (
                          <div key={report.id} className="report-detail">
                            <p><strong>Reported by:</strong> {report.username}</p>
                            <p><strong>Report Type:</strong> {report.reasonType || 'NA'}</p>
                            <p><strong>Reason:</strong> {report.reason}</p>
                            <p><strong>Date:</strong> {formatDate(report.createdAt || report.date)}</p>
                          </div>
                        ))}
                      </div>
                      <div className="report-actions">
                        <button 
                          className="btn btn-info btn-sm"
                          onClick={() => handleViewOnMap(restroom)}
                          title="View restroom on map"
                        >
                          🗺️ Map
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteRestroom(restroom.id)}
                        >
                          Delete Restroom
                        </button>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDismissReports(restroom.id)}
                        >
                          Dismiss Reports
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-reports">
                    <p>No reported content at this time.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-tab">
            <div className="tab-header">
              <h2>⚙️ System Settings</h2>
              <p>Configure system-wide settings for CrapperMapper</p>
            </div>

            {/* System Information */}
            <div className="settings-section system-info-section">
              <div className="section-header">
                <h3>ℹ️ System Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">App Version:</span>
                  <span className="info-value">{APP_VERSION}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Latest Update:</span>
                  <span className="info-value">{VERSION_NOTES}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Database:</span>
                  <span className="info-value">Firebase Firestore</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Verification Radius:</span>
                  <span className="info-value">15 meters (49 feet)</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Cache Duration:</span>
                  <span className="info-value">60 seconds</span>
                </div>
              </div>
            </div>

            <div className="settings-section migration-section">
              <div className="section-header">
                <h3>🔧 Data Maintenance</h3>
                <p className="section-description">
                  Tools to fix data inconsistencies and maintain system health.
                </p>
              </div>

              <div className="migration-subsection">
                <h4>🌍 Geohash Repair</h4>
                <p className="subsection-description">
                  Populate missing geohashes for restrooms to ensure they appear on the map.
                </p>
                <div className="migration-actions">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleFixGeohashes}
                    disabled={migrationLoading}
                  >
                    {migrationLoading ? '⌛ Fixing...' : '🔄 Repair Geohashes'}
                  </button>
                </div>
              </div>

              <div className="migration-subsection">
                <h4>📈 Leaderboard Cache</h4>
                <p className="subsection-description">
                  Rebuild TP Tower leaderboards (24h/7d/30d/All) and the TP distribution cache used for rank estimates.
                </p>
                <div className="migration-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleRefreshTpLeaderboardsNow}
                    disabled={refreshTpLoading}
                  >
                    {refreshTpLoading ? '⌛ Refreshing...' : '🔁 Refresh TP Leaderboards Now'}
                  </button>
                </div>
                {lastRefreshInfo && (
                  <div className="migration-status">
                    <small>
                      Last run: {lastRefreshInfo.time.toLocaleString()}
                    </small>
                  </div>
                )}
              </div>

              <div className="migration-subsection">
                <h4>🧩 Gamification Rules</h4>
                <p className="subsection-description">
                  Seed or update the initial TP rules, milestones, titles, and badges at
                  <code> /system_config/gamification_rules</code>.
                </p>
                <div className="migration-actions" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={async () => {
                      try {
                        setSeedRulesLoading(true);
                        const functions = getFunctions(firebaseService.app);
                        const seedFn = httpsCallable(functions, 'SeedGamificationRulesNow');
                        const result = await seedFn({
                          database_id_hash: localStorage.getItem('database_id_hash') || '',
                          forceOverwrite: seedForceOverwrite
                        });
                        if (result?.data?.success) {
                          setLastSeedInfo({ time: new Date(), message: result.data.message || 'Seeded.' });
                          toast.success(result.data.message || 'Gamification rules updated.');
                        } else {
                          toast.error(result?.data?.message || 'Seeding failed.');
                        }
                      } catch (error) {
                        console.error('Error seeding gamification rules:', error);
                        toast.error(error.message || 'Failed to seed gamification rules');
                      } finally {
                        setSeedRulesLoading(false);
                      }
                    }}
                    disabled={seedRulesLoading}
                  >
                    {seedRulesLoading ? '⌛ Seeding...' : '🌱 Seed/Update Gamification Rules'}
                  </button>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={seedForceOverwrite}
                      onChange={(e) => setSeedForceOverwrite(e.target.checked)}
                    />
                    Force overwrite
                  </label>
                </div>
                {lastSeedInfo && (
                  <div className="migration-status">
                    <small>
                      Last run: {lastSeedInfo.time.toLocaleString()} — {lastSeedInfo.message}
                    </small>
                  </div>
                )}
              </div>

              <div className="migration-subsection">
                <h4>🧮 Backfill Gamification Baseline</h4>
                <p className="subsection-description">
                  Compute and write baseline TP, titles, and badges for existing users. Runs in small batches; you can
                  re-run until complete. Uses checkpoint at <code>system_jobs/gamification_backfill</code>.
                </p>
                <div className="migration-actions" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Batch size
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={backfillLimit}
                      onChange={(e) => setBackfillLimit(Number(e.target.value) || 1)}
                      style={{ width: 90 }}
                    />
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={backfillResume}
                      onChange={(e) => setBackfillResume(e.target.checked)}
                    />
                    Resume from checkpoint
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={backfillResetCheckpoint}
                      onChange={(e) => setBackfillResetCheckpoint(e.target.checked)}
                    />
                    Reset checkpoint first
                  </label>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={async () => {
                      try {
                        setBackfillLoading(true);
                        const functions = getFunctions(firebaseService.app);
                        const backfillFn = httpsCallable(functions, 'BackfillGamificationNow');
                        const result = await backfillFn({
                          database_id_hash: localStorage.getItem('database_id_hash') || '',
                          limit: backfillLimit,
                          resume: backfillResume,
                          resetCheckpoint: backfillResetCheckpoint
                        });
                        if (result?.data?.success) {
                          const info = {
                            time: new Date(),
                            message: result.data.message || 'Backfill run complete.',
                            processed: result.data.processed || 0,
                            lastProcessedUserId: result.data.lastProcessedUserId || null
                          };
                          setLastBackfillInfo(info);
                          toast.success(info.message);
                        } else {
                          toast.error(result?.data?.message || 'Backfill failed.');
                        }
                      } catch (error) {
                        console.error('Error running gamification backfill:', error);
                        toast.error(error.message || 'Failed to run backfill');
                      } finally {
                        setBackfillLoading(false);
                      }
                    }}
                    disabled={backfillLoading}
                  >
                    {backfillLoading ? '⌛ Backfilling...' : '🧮 Run Backfill Now'}
                  </button>
                </div>
                {lastBackfillInfo && (
                  <div className="migration-status">
                    <small>
                      Last run: {lastBackfillInfo.time.toLocaleString()} — {lastBackfillInfo.message}
                      {lastBackfillInfo.lastProcessedUserId ? ` (last user: ${lastBackfillInfo.lastProcessedUserId})` : ''}
                    </small>
                  </div>
                )}
              </div>
            </div>

        {/* Migration Tools - COMMENTED OUT since all users are now migrated
            <div className="settings-section migration-section-old">
              <div className="section-header">
                <h3>🔧 Migration Tools</h3>
                <p className="section-description">
                  One-time operations for migrating existing data to the new verification system.
                </p>
              </div>
              
              {/* User Document Migration - COMMENTED OUT
              <div className="migration-subsection">
                <h4>👤 User Document ID Migration</h4>
                <p className="subsection-description">
                  Update user documents to use Firebase Auth UID as document ID for proper security rules.
                </p>
                
                <div className="migration-actions">
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={handleCheckUserDocMigration}
                    disabled={migrationLoading}
                  >
                    📊 Check Migration Status
                  </button>
                  
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleMigrateUserDocuments}
                    disabled={migrationLoading}
                  >
                    🔄 Migrate User Documents
                  </button>
                  
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleRefreshUsers}
                    disabled={migrationLoading}
                  >
                    🔄 Refresh Users List
                  </button>
                </div>
                
                {userDocMigrationStatus && (
                  <div className="migration-status">
                    <h5>Migration Status:</h5>
                    <div className="status-details">
                      <div className="status-item">
                        <span className="status-label">Total Users:</span>
                        <span className="status-value">{userDocMigrationStatus.total || 0}</span>
                      </div>
                      <div className="status-item">
                        <span className="status-label">Need Migration:</span>
                        <span className="status-value warning">{userDocMigrationStatus.needingMigration || 0}</span>
                      </div>
                      <div className="status-item">
                        <span className="status-label">Already Migrated:</span>
                        <span className="status-value success">{userDocMigrationStatus.migrated || 0}</span>
                      </div>
                      {userDocMigrationStatus.usersNeedingMigration?.length > 0 && (
                        <div className="users-needing-migration">
                          <h6>Users needing migration:</h6>
                          <ul>
                            {userDocMigrationStatus.usersNeedingMigration.map(user => (
                              <li key={user.id}>
                                {user.username} ({user.id} → {user.firebaseUid}) 
                                {user.isAdmin && <span className="admin-badge">ADMIN</span>}
                              </li>
                            ))}
                          </div>
              */}
              
              {/* Review ID Migration - HIDDEN for production security
              <div className="migration-subsection">
                <h4>📝 Review ID Migration</h4>
                <p className="subsection-description">
                  Fix "Review not found in any restroom" errors by adding proper IDs to older reviews.
                  This ensures the like functionality works for all reviews.
                </p>
                
                <div className="migration-actions">
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={handleCheckReviewIdMigration}
                    disabled={reviewIdMigrationLoading}
                  >
                    📊 Check Review IDs
                  </button>
                  
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleMigrateReviewIds}
                    disabled={reviewIdMigrationLoading || !reviewIdMigrationStatus?.migrationNeeded}
                  >
                    {reviewIdMigrationLoading ? '🔄 Migrating...' : '🔧 Fix Review IDs'}
                  </button>
                </div>
                
                {reviewIdMigrationStatus && (
                  <div className="migration-status">
                    <h5>Review ID Status:</h5>
                    <div className="migration-stats">
                      <div className="stat">Total restrooms: {reviewIdMigrationStatus.totalRestrooms}</div>
                      <div className="stat">Restrooms with reviews: {reviewIdMigrationStatus.restroomsWithReviews}</div>
                      <div className="stat">Total reviews: {reviewIdMigrationStatus.totalReviews}</div>
                      <div className={`stat ${reviewIdMigrationStatus.reviewsWithoutIds > 0 ? 'warning' : 'success'}`}>
                        {reviewIdMigrationStatus.reviewsWithoutIds > 0 ? '⚠️' : '✅'} 
                        Reviews needing IDs: {reviewIdMigrationStatus.reviewsWithoutIds}
                      </div>
                      <div className="stat success">Reviews with proper IDs: {reviewIdMigrationStatus.reviewsWithProperIds}</div>
                    </div>
                    
                    {reviewIdMigrationStatus.migrationNeeded && (
                      <div className="migration-warning">
                        <strong>⚠️ Migration Recommended</strong><br/>
                        Some reviews are missing proper IDs which can cause like functionality to fail.
                        Running the migration will fix these issues.
                      </div>
                    )}
                  </div>
                )}
                
                {reviewIdMigrationResults && (
                  <div className="migration-results">
                    <h5>Migration Results:</h5>
                    <div className="migration-stats">
                      <div className="stat success">✅ Restrooms updated: {reviewIdMigrationResults.restroomsUpdated}</div>
                      <div className="stat success">✅ Reviews migrated: {reviewIdMigrationResults.reviewsMigrated}</div>
                      <div className="stat">Reviews skipped: {reviewIdMigrationResults.reviewsSkipped}</div>
                    </div>
                    <div className="migration-timestamp">
                      Completed: {new Date(reviewIdMigrationResults.timestamp).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
              */}
              
              {/* Restroom Verification Migration - This section is still active */}
              <div className="migration-subsection">
                <h4>🚽 Restroom Verification Migration</h4>
                <div className="migration-actions">
                  <button 
                    className="btn btn-primary btn-lg bulk-verify-btn"
                    onClick={handleBulkVerify}
                  >
                    ✅ Bulk Verify All Existing Restrooms
                  </button>
                  <p className="migration-note">
                    This will mark all existing restrooms in the database as verified. 
                    Run this once to migrate to the new verification system (v6.16.0).
                    <br/>
                    <strong>Note:</strong> Only unverified restrooms will be updated.
                  </p>
                </div>
              </div>
            {/* End of commented migration tools section */}
          </div>
        )}

        {/* QR Management Tab */}
        {activeTab === 'qr-management' && (
          <div className="qr-management-tab">
            <div className="tab-header">
              <h2>📱 QR Code Management</h2>
              <p>Generate and track QR codes for referral marketing campaigns</p>
            </div>

            {/* QR Code Generator Section */}
            <QRCodeGeneratorWithHistory />

            {/* QR Analytics Section */}
            <div className="qr-analytics-section">
              <h3>📊 QR Code Performance</h3>
              <p>Track your QR code campaigns in PostHog</p>
              
              <div className="qr-instructions">
                <h4>How to track performance:</h4>
                <ol>
                  <li>Go to your <strong>PostHog Dashboard</strong></li>
                  <li>Filter events by <code>qr_code_scanned</code></li>
                  <li>Group by <code>referral_code</code> to see which codes perform best</li>
                  <li>Track conversions with <code>referral_conversion</code> events</li>
                </ol>
                
                <div className="qr-metrics">
                  <h5>Key Metrics to Monitor:</h5>
                  <ul>
                    <li><strong>Scan Rate:</strong> How many people scan each QR code</li>
                    <li><strong>Conversion Rate:</strong> % of scans that become registered users</li>
                    <li><strong>Top Performing Codes:</strong> Which locations/campaigns work best</li>
                    <li><strong>User Quality:</strong> Do QR users stay active longer?</li>
                  </ul>
                </div>
              </div>
            </div>

            <style jsx>{`
              .qr-analytics-section {
                margin-top: 30px;
                padding: 30px;
                background: white;
                border-radius: 12px;
                border: 1px solid #e9ecef;
              }

              .qr-analytics-section h3 {
                color: #495057;
                margin-bottom: 10px;
              }

              .qr-analytics-section p {
                color: #6c757d;
                margin-bottom: 25px;
              }

              .qr-instructions {
                background: #f8f9fa;
                padding: 25px;
                border-radius: 8px;
                border-left: 4px solid #5752e8;
              }

              .qr-instructions h4 {
                color: #231F20;
                margin-bottom: 15px;
              }

              .qr-instructions ol {
                margin-bottom: 25px;
                padding-left: 20px;
              }

              .qr-instructions li {
                margin-bottom: 10px;
                color: #495057;
                line-height: 1.6;
              }

              .qr-instructions code {
                background: #e9ecef;
                padding: 2px 6px;
                border-radius: 3px;
                font-family: monospace;
                font-size: 0.9em;
                color: #c7254e;
              }

              .qr-metrics h5 {
                color: #495057;
                margin-bottom: 15px;
              }

              .qr-metrics ul {
                padding-left: 20px;
              }

              .qr-metrics li {
                margin-bottom: 8px;
                color: #6c757d;
                line-height: 1.5;
              }

              .qr-metrics strong {
                color: #495057;
              }
            `}</style>
          </div>
        )}

        {activeTab === 'queries' && (
          <div className="queries-tab">
            <div className="tab-header">
              <h2>🔍 FireSQL Queries</h2>
              <p>Query Firestore using SQL syntax (e.g., <code>SELECT * FROM restrooms WHERE verified = true</code>)</p>
            </div>

            <div className="query-container">
              <textarea
                className="query-input"
                value={sqlQuery}
                onChange={(e) => setSqlQuery(e.target.value)}
                placeholder="SELECT * FROM restrooms LIMIT 10"
                rows="5"
              />
              <div className="query-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleRunQuery}
                  disabled={queryLoading}
                >
                  {queryLoading ? '⏳ Running...' : '🚀 Run Query'}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => { setSqlQuery(''); setQueryResults(null); setQueryError(null); }}
                  disabled={queryLoading}
                >
                  🧹 Clear
                </button>
              </div>
            </div>

            {queryError && (
              <div className="query-error">
                <strong>❌ Error:</strong> {queryError}
              </div>
            )}

            {queryResults && (
              <div className="query-results-container">
                <div className="results-header">
                  <h3>📊 Results ({queryResults.length})</h3>
                  <button 
                    className="btn btn-sm btn-outline"
                    onClick={() => {
                      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(queryResults, null, 2));
                      const downloadAnchorNode = document.createElement('a');
                      downloadAnchorNode.setAttribute("href",     dataStr);
                      downloadAnchorNode.setAttribute("download", "query_results.json");
                      document.body.appendChild(downloadAnchorNode);
                      downloadAnchorNode.click();
                      downloadAnchorNode.remove();
                    }}
                  >
                    📥 Export JSON
                  </button>
                </div>
                
                {queryResults.length > 0 ? (
                  <div className="results-scroll">
                    <ReactJson 
                      src={queryResults} 
                      theme="monokai"
                      collapsed={1}
                      displayDataTypes={false}
                      displayObjectSize={true}
                      enableClipboard={true}
                      style={{ 
                        padding: '16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        backgroundColor: '#2d2d2d'
                      }}
                      onEdit={false}
                      onAdd={false}
                      onDelete={false}
                    />
                  </div>
                ) : (
                  <div className="no-results">No documents matched the query.</div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'gameconfig' && (
          <div className="gameconfig-tab">
            <GameConfig />
          </div>
        )}

        {activeTab === 'migration' && (
          <div className="migration-tab">
            <div className="tab-header">
              <h2>🔐 Legacy Account Migration</h2>
              <p>Migrate existing accounts to Firebase Authentication</p>
            </div>

            {/* Info Box */}
            <div className="settings-section">
              <div className="section-header">
                <h3>📋 What This Does</h3>
              </div>
              <ul className="migration-info-list">
                <li>Creates Firebase Auth account with secure password</li>
                <li>Links existing Firestore profile to Firebase Auth</li>
                <li>Removes plain text password from Firestore</li>
                <li>Preserves all user data, restrooms, and reviews</li>
              </ul>
              <div className="warning-box">
                <strong>⚠️ Important:</strong> Users must have an <strong>email</strong> in their profile. 
                After migration, users login with their <strong>original password</strong>.
              </div>
            </div>

            {/* Check Status - COMMENTED OUT since migration is complete */}
            {/* <div className="settings-section">
              <div className="section-header">
                <h3>1️⃣ Check Migration Status</h3>
              </div>
              <div className="migration-form">
                <input
                  type="text"
                  value={migrationUsername}
                  onChange={(e) => setMigrationUsername(e.target.value)}
                  placeholder="Enter username"
                  disabled={migrationLoading}
                  className="migration-input"
                />
                <button 
                  onClick={handleCheckStatus} 
                  disabled={migrationLoading}
                  className="btn btn-secondary"
                >
                  Check Status
                </button>
              </div>

              {migrationStatus && migrationStatus.exists && (
                <div className={`migration-status ${migrationStatus.isMigrated ? 'migrated' : 'not-migrated'}`}>
                  <h4>Status for: {migrationStatus.username}</h4>
                  <div className="status-grid">
                    <div><strong>Email:</strong> {migrationStatus.email}</div>
                    <div><strong>Migrated:</strong> {migrationStatus.isMigrated ? '✅ Yes' : '❌ No'}</div>
                    <div><strong>Admin:</strong> {migrationStatus.isAdmin ? '👑 Yes' : 'No'}</div>
                    {migrationStatus.firebaseUid && (
                      <div><strong>Firebase UID:</strong> <code>{migrationStatus.firebaseUid.substring(0, 20)}...</code></div>
                    )}
                  </div>
                </div>
              )}
            </div>
            */}

            {/* Single Migration */}
            {/* <div className="settings-section">
              <div className="section-header">
                <h3>2️⃣ Migrate Single Account</h3>
              </div>
              <div className="migration-form">
                <input
                  type="text"
                  value={migrationUsername}
                  onChange={(e) => setMigrationUsername(e.target.value)}
                  placeholder="Enter username to migrate"
                  disabled={migrationLoading}
                  className="migration-input"
                />
                <button 
                  onClick={handleSingleMigration} 
                  disabled={migrationLoading}
                  className="btn btn-primary"
                >
                  {migrationLoading ? 'Migrating...' : 'Migrate Account'}
                </button>
              </div>
            </div>

            {/* Batch Migration */}
            {/* <div className="settings-section">
              <div className="section-header">
                <h3>3️⃣ Migrate Multiple Accounts (Batch)</h3>
              </div>
              <div className="migration-form-vertical">
                <textarea
                  value={migrationBatch}
                  onChange={(e) => setMigrationBatch(e.target.value)}
                  placeholder="Enter usernames (one per line)"
                  rows="6"
                  disabled={migrationLoading}
                  className="migration-textarea"
                />
                <button 
                  onClick={handleBatchMigration} 
                  disabled={migrationLoading}
                  className="btn btn-primary"
                >
                  {migrationLoading ? 'Migrating...' : 'Migrate All'}
                </button>
              </div>
            </div>

            {/* Results - COMMENTED OUT since migration is complete */}
            {/* {migrationResults.length > 0 && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>📊 Migration Results</h3>
                </div>
                {migrationResults.map((result, index) => (
                  <div key={index} className="migration-result-card">
                    {result.type === 'batch' ? (
                      <div>
                        <h4>Batch Migration Summary</h4>
                        <div className="migration-stats">
                          <div className="stat">Total: {result.total}</div>
                          <div className="stat success">✅ Migrated: {result.successful.length}</div>
                          <div className="stat warning">⚠️ Already Migrated: {result.alreadyMigrated.length}</div>
                          <div className="stat error">❌ Failed: {result.failed.length}</div>
                        </div>

                        {result.successful.length > 0 && (
                          <div className="result-list">
                            <strong>✅ Successfully Migrated:</strong>
                            <ul>
                              {result.successful.map(u => <li key={u}>{u}</li>)}
                            </ul>
                          </div>
                        )}

                        {result.failed.length > 0 && (
                          <div className="result-list error">
                            <strong>❌ Failed:</strong>
                            <ul>
                              {result.failed.map(f => (
                                <li key={f.username}><strong>{f.username}:</strong> {f.error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <h4>{result.success ? '✅ Success' : '❌ Failed'}</h4>
                        <p>{result.message}</p>
                        {result.firebaseUid && (
                          <div><strong>Firebase UID:</strong> <code>{result.firebaseUid}</code></div>
                        )}
                        {result.email && (
                          <div><strong>Email:</strong> {result.email}</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            */}
          </div>
        )}
      </div>

      {showMatchModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <div className="modal-header">
              <h3>Verify Restroom: {matchingRestroom?.name}</h3>
              <button className="close-btn" onClick={() => setShowMatchModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Select the corresponding Google Place to verify this restroom:</p>
              {matchingLoading ? (
                <div className="loading-spinner">Searching nearby places...</div>
              ) : nearbyPlaces.length > 0 ? (
                <div className="places-list">
                  {nearbyPlaces.map(place => (
                    <div key={place.placeId} className="place-item">
                      <div className="place-info">
                        <strong>{place.name}</strong>
                        <p className="place-address">{place.address}</p>
                        <small className="place-id">ID: {place.placeId}</small>
                      </div>
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={() => handleMatchRestroom(place)}
                        disabled={matchingLoading}
                      >
                        Match & Verify
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-places">No nearby places found from Google.</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMatchModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;